import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpDown,
  Building2,
  CircleX,
  DoorOpen,
  Droplet,
  Hammer,
  Layers,
  PaintRoller,
  Router,
  ShieldCheck,
  Trees,
  Waves,
  Zap,
} from "lucide-react";

import "../../styles/WoodsSpecificationAccordion.css";
import { woodsSpecificationData } from "../../data/woodsSpecificationData";

const iconMap = {
  foundation_structure: Building2,
  plastering: Hammer,
  flooring: Layers,
  toilets: Droplet,
  plumbing_system: Waves,
  doors_windows: DoorOpen,
  paint: PaintRoller,
  electrical_fixture: Zap,
  security_network: ShieldCheck,
  communication_network: Router,
  landscaping: Trees,
  elevator: ArrowUpDown,
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export default function WoodsSpecificationAccordion() {
  const [selectedId, setSelectedId] = useState(null);
  const hasData = Boolean(woodsSpecificationData && woodsSpecificationData.length > 0);
  const selectedItem =
    woodsSpecificationData.find((item) => item.id === selectedId) || null;
  const SelectedIcon = selectedItem ? iconMap[selectedItem.id] || Building2 : Building2;

  useEffect(() => {
    if (!selectedItem) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedId(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedItem]);

  if (!hasData) return null;

  return (
    <section
      className="specification-section woods-spec-section py-5"
      id="serene-specifications"
    >
      <div className="woods-spec-aurora" aria-hidden="true"></div>
      <div className="container woods-spec-container">
        <header className="woods-spec-header">
          <div className="woods-spec-eyebrow">Specification Schedule</div>
          <h2 className="woods-spec-title">Vessella Woods Specifications</h2>
          <p className="woods-spec-subtitle">
            Thoughtfully chosen materials, systems, and finishes that shape every
            villa in the community.
          </p>
        </header>

        <div className="woods-spec-grid">
          {woodsSpecificationData.map((item, index) => {
            const Icon = iconMap[item.id] || Building2;

            return (
              <motion.article
                key={item.id}
                className="woods-spec-card d-flex align-items-center justify-content-between"
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, ease: "easeOut", delay: index * 0.05 }}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedId(item.id);
                  }
                }}
              >
                <div className="woods-spec-card-top">
                  <div className="woods-spec-icon" aria-hidden="true">
                    <Icon size={22} />
                  </div>
                  <span className="woods-spec-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="woods-spec-card-copy d-flex align-items-center justify-content-between flex-grow-1">
                  <h3 className="woods-spec-card-title">{item.title}</h3>
                  <span className="woods-spec-arrow" aria-hidden="true">
                    <ArrowRight size={18} />
                  </span>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {selectedItem && (
        <div
          className="woods-spec-modal-backdrop"
          role="presentation"
          onClick={() => setSelectedId(null)}
        >
          <div
            className="woods-spec-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="woods-spec-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="woods-spec-modal-close"
              aria-label="Close specifications popup"
              onClick={() => setSelectedId(null)}
            >
              <CircleX size={22} />
            </button>

            <div className="woods-spec-modal-top d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div className="woods-spec-icon woods-spec-modal-icon" aria-hidden="true">
                  <SelectedIcon size={20} />
                </div>
                <div>
                  <div className="woods-spec-modal-kicker">Specification Details</div>
                  <h3 id="woods-spec-modal-title" className="woods-spec-modal-title">
                    {selectedItem.title}
                  </h3>
                </div>
              </div>

              <div className="woods-spec-modal-count">
                {String(
                  woodsSpecificationData.findIndex((item) => item.id === selectedItem.id) + 1
                ).padStart(2, "0")}
              </div>
            </div>

            <ul className="woods-spec-list">
              {selectedItem.points.map((point, idx) => (
                <li className="woods-spec-item" key={`${selectedItem.id}-${idx}`}>
                  <span className="woods-spec-bullet" aria-hidden="true"></span>
                  <div className="woods-spec-item-body">
                    {point.label && (
                      <span className="woods-spec-chip">{point.label}</span>
                    )}
                    <span className="woods-spec-text">{point.text}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
